import { useState, useCallback, useRef, useEffect } from "react";
import type {
  EncounterState,
  GameMasterResponse,
  GameIntent,
  NarrativeEntry,
  AlertLevel,
  SpatialState,
  DialogueLine,
  ActiveOrder,
  ViewscreenMode,
  HailState,
} from "../types/game";

// ── Captain's log entry ──
export interface LogEntry {
  stardate: string;
  text: string;
  timestamp: number;
}

// ── Save/restore helpers ──
const SAVE_KEY = "starbridge-save-v1";

interface SavedSession {
  sessionId: string;
  encounterState: EncounterState;
  narrativeLog: NarrativeEntry[];
  alertLevel: AlertLevel;
  captainsLog: LogEntry[];
  baseStardate: number;
  missionStartMs: number;
  lockedTargets?: string[];
}

function loadSavedSession(): SavedSession | null {
  try {
    const raw = localStorage.getItem(SAVE_KEY);
    return raw ? (JSON.parse(raw) as SavedSession) : null;
  } catch { return null; }
}

function saveSession(s: SavedSession) {
  try { localStorage.setItem(SAVE_KEY, JSON.stringify(s)); } catch {}
}

function clearSavedSession() {
  try { localStorage.removeItem(SAVE_KEY); } catch {}
}

// ── Stardate helpers ──
// TNG era: base ~47000. 1 stardate unit ≈ 2.4 hours of mission time.
function randomBaseStardate() {
  return 47000 + Math.floor(Math.random() * 999);
}

export function currentStardate(base: number, startMs: number): string {
  const days = (Date.now() - startMs) / 86_400_000;
  return (base + days * 10).toFixed(1);
}
import { SceneCommandExecutor } from "../systems/SceneCommandExecutor";
import { TimerSystem } from "../systems/TimerSystem";
import { IntentResolver } from "../systems/IntentResolver";
import { CombatSimulator } from "../systems/CombatSimulator";
import { AmbientEventSystem } from "../systems/AmbientEventSystem";
import { soundSystem } from "../systems/SoundSystem";
import { ttsService } from "../systems/TTSService";
import { MissionLogger } from "../systems/MissionLogger";
import { setCameraMode } from "../systems/cameraState";

const API_BASE = import.meta.env.VITE_API_URL || "http://localhost:8787";

// Portrait URLs are set by fetchPortrait after generation.
// getPortraitUrl is only used when we know the portrait is already cached (e.g., resume).
function getPortraitUrl(_characterId: string): string | null {
  return null; // Let fetchPortrait handle it — avoids broken static URLs
}

// Default spatial state until server provides one
const DEFAULT_SPATIAL: SpatialState = {
  playerShip: {
    position: { x: 0, y: 0, z: 0 },
    heading: { x: 1, y: 0, z: 0 },
    speed: "all_stop",
    shields: "down",
  },
  entities: [],
};

interface PreloadedEncounter {
  sessionId: string;
  encounterState: EncounterState;
  opening: any;
}

export function useGameSession(preloaded?: PreloadedEncounter) {
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [encounterState, setEncounterState] = useState<EncounterState | null>(null);
  const [narrativeLog, setNarrativeLog] = useState<NarrativeEntry[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isBusy, setIsBusy] = useState(false);
  const [alertLevel, setAlertLevel] = useState<AlertLevel>("normal");
  const [orders, setOrders] = useState<ActiveOrder[]>([]);
  const [liveSpatial, setLiveSpatial] = useState<SpatialState | null>(null);
  const [viewscreenMode, setViewscreenMode] = useState<ViewscreenMode>({ mode: "space" });
  const [viewscreenImage, setViewscreenImage] = useState<string | null>(null);
  const [hailState, setHailState] = useState<HailState>({
    active: false,
    channelMuted: false,
    targetId: null,
    targetName: null,
  });
  const [captainsLog, setCaptainsLog] = useState<LogEntry[]>([]);
  const [baseStardate]  = useState(() => randomBaseStardate());
  const [missionStartMs, setMissionStartMs] = useState(0);
  const [hasSave, setHasSave] = useState(() => loadSavedSession() !== null);
  const [showChart, setShowChart] = useState(false);
  const [lockedTargets, setLockedTargets] = useState<Set<string>>(new Set());
  const lockedTargetsRef = useRef<Set<string>>(lockedTargets);
  lockedTargetsRef.current = lockedTargets;

  const addLockedTarget = useCallback((targetId: string) => {
    setLockedTargets(prev => {
      if (prev.has(targetId)) return prev;
      const next = new Set(prev);
      next.add(targetId);
      return next;
    });
  }, []);

  const removeLockedTarget = useCallback((targetId: string) => {
    setLockedTargets(prev => {
      if (!prev.has(targetId)) return prev;
      const next = new Set(prev);
      next.delete(targetId);
      return next;
    });
  }, []);
  const [missionResult, setMissionResult] = useState<{
    type: string;
    summary: string;
  } | null>(null);

  const entryIdRef = useRef(0);
  const executorRef = useRef<SceneCommandExecutor | null>(null);
  const timerRef = useRef<TimerSystem | null>(null);
  const sessionIdRef = useRef<string | null>(null);
  const intentResolverRef = useRef<IntentResolver | null>(null);
  const combatSimRef = useRef<CombatSimulator | null>(null);
  const ambientRef = useRef<AmbientEventSystem | null>(null);
  const loggerRef = useRef<MissionLogger | null>(null);
  const rafRef = useRef<number>(0);
  const lastTimeRef = useRef(0);
  const ambientTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const missionResolvedRef = useRef(false);

  // Keep refs in sync for beforeunload handler
  const encounterRef = useRef(encounterState);
  encounterRef.current = encounterState;
  const alertRef = useRef(alertLevel);
  alertRef.current = alertLevel;
  const logRef = useRef(captainsLog);
  logRef.current = captainsLog;
  const narrativeRef = useRef(narrativeLog);
  narrativeRef.current = narrativeLog;

  useEffect(() => {
    sessionIdRef.current = sessionId;
  }, [sessionId]);

  // Save live spatial state on page unload so heading/position survive reload
  useEffect(() => {
    const handleUnload = () => {
      const sid = sessionIdRef.current;
      const enc = encounterRef.current;
      if (!sid || !enc) return;
      // Flush mission log to server before page closes
      loggerRef.current?.saveBeacon();
      const live = executorRef.current?.currentSpatialState;
      const stateToSave = live ? {
        ...enc,
        spatialState: {
          ...enc.spatialState,
          playerShip: {
            ...enc.spatialState.playerShip,
            position: live.playerShip.position,
            heading: live.playerShip.heading,
            speed: live.playerShip.speed,
            shields: live.playerShip.shields,
          },
        },
      } : enc;
      saveSession({
        sessionId: sid,
        encounterState: stateToSave,
        narrativeLog: narrativeRef.current.slice(-100),
        alertLevel: alertRef.current,
        captainsLog: logRef.current,
        baseStardate,
        missionStartMs,
        lockedTargets: [...lockedTargetsRef.current],
      });
    };
    window.addEventListener("beforeunload", handleUnload);
    return () => window.removeEventListener("beforeunload", handleUnload);
  }, [baseStardate, missionStartMs]);

  const addLogEntry = useCallback((text: string) => {
    setCaptainsLog(prev => {
      // Normalize for dedup: strip weapon specifics and common variations
      const normalize = (s: string) =>
        s.toLowerCase().trim()
          .replace(/\b(phaser|disruptor|torpedo|photon|quantum|plasma)\b/g, "weapons")
          .replace(/\blocked on the ship\b/, "locked on target")
          .slice(0, 50);
      const key = normalize(text);
      const recent = prev.slice(-5);
      if (recent.some(e => normalize(e.text) === key)) return prev;
      return [
        ...prev,
        { stardate: currentStardate(baseStardate, missionStartMs), text, timestamp: Date.now() },
      ];
    });
  }, [baseStardate, missionStartMs]);

  // ── Auto-save whenever state changes ──
  useEffect(() => {
    if (!sessionId || !encounterState) return;
    // Merge executor's live spatial state so heading/position survive reload
    let stateToSave = encounterState;
    if (executorRef.current) {
      const live = executorRef.current.currentSpatialState;
      stateToSave = {
        ...encounterState,
        spatialState: {
          ...encounterState.spatialState,
          playerShip: {
            ...encounterState.spatialState.playerShip,
            position: live.playerShip.position,
            heading: live.playerShip.heading,
            speed: live.playerShip.speed,
            shields: live.playerShip.shields,
          },
        },
      };
    }
    saveSession({
      sessionId,
      encounterState: stateToSave,
      narrativeLog: narrativeLog.slice(-100),
      alertLevel,
      captainsLog,
      baseStardate,
      missionStartMs,
      lockedTargets: [...lockedTargets],
    });
  }, [sessionId, encounterState, narrativeLog, alertLevel, captainsLog, baseStardate, missionStartMs, lockedTargets]);

  const addEntry = useCallback(
    (type: NarrativeEntry["type"], text: string, speaker?: string, role?: string, sidebar?: boolean) => {
      const entry: NarrativeEntry = {
        id: `entry-${entryIdRef.current++}`,
        type,
        text,
        speaker,
        role,
        timestamp: Date.now(),
        sidebar,
      };
      setNarrativeLog((prev) => [...prev, entry]);
    },
    []
  );

  // ── Process dialogue with delays + sliding window dedup ──
  const recentDialogueRef = useRef<string[]>([]);
  const processDialogue = useCallback(
    (dialogue: DialogueLine[]) => {
      const seen = recentDialogueRef.current;
      for (const line of dialogue) {
        const key = `${line.speaker}:${line.text.slice(0, 60).toLowerCase().trim()}`;
        if (seen.includes(key)) continue;
        seen.push(key);
        // Sliding window: keep last 30 entries
        while (seen.length > 30) seen.shift();

        const showLine = () => {
          addEntry("dialogue", line.text, line.speaker, line.role, line.sidebar);
          loggerRef.current?.logDialogue(line);
          ttsService.speak(line.speaker, line.text);
          // Resolve intents attached to this dialogue line
          if (line.intents?.length && intentResolverRef.current) {
            loggerRef.current?.logIntents(line.intents);
            intentResolverRef.current.resolve(line.intents);
          }
        };

        if (line.priority === "immediate" || line.delay === 0) {
          showLine();
        } else {
          setTimeout(showLine, line.delay * 1000);
        }
      }
    },
    [addEntry]
  );

  // ── Portrait system — load static pre-rendered portraits ���─
  const fetchPortrait = useCallback(async (characterId: string, description: string) => {
    try {
      const res = await fetch(`${API_BASE}/api/portrait`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ characterId, description }),
      });
      if (res.ok) {
        const data = await res.json();
        if (data.imageUrl) setViewscreenImage(data.imageUrl);
      }
    } catch { /* non-blocking — viewscreen falls back to placeholder */ }
  }, []);

  // ── Process a full AI response ──
  const processGMResponse = useCallback(
    (response: GameMasterResponse) => {
      try {
        console.log("[GM Response]", {
          dialogue: response.dialogue?.length ?? 0,
          intents: response.intents?.length ?? 0,
          timedEvents: response.timedEvents?.length ?? 0,
          stateUpdates: response.stateUpdates ? Object.keys(response.stateUpdates) : [],
        });
        if (response.intents?.length) {
          console.log("[GM Intents]", JSON.stringify(response.intents, null, 2));
        }

        // Mission logging
        loggerRef.current?.logGMResponse(response);

        // Dialogue
        if (response.dialogue?.length) {
          processDialogue(response.dialogue);
        }

        // Register fire intents with the combat simulator. Player or NPC
        // fire opens the auto-fire gate (Phase 4.3); LLM-generated npc_fire
        // also sets a cooldown so the simulator doesn't double-shoot.
        const allIntents = [
          ...(response.intents ?? []),
          ...(response.dialogue?.flatMap(l => l.intents ?? []) ?? []),
        ];
        for (const i of allIntents) {
          if (i.type === "npc_fire" && i.source) {
            combatSimRef.current?.registerLLMFire(i.source);
          }
          if (i.type === "fire_weapons") {
            combatSimRef.current?.registerPlayerFire();
          }
        }

        // Intents → IntentResolver (only those NOT already on dialogue lines)
        if (response.intents?.length && intentResolverRef.current) {
          // Collect intent types already attached to dialogue lines
          const dialogueIntentKeys = new Set<string>();
          if (response.dialogue?.length) {
            for (const line of response.dialogue) {
              if (line.intents?.length) {
                for (const i of line.intents) {
                  dialogueIntentKeys.add(`${i.type}:${i.target || ""}`);
                }
              }
            }
          }
          const dedupedIntents = dialogueIntentKeys.size > 0
            ? response.intents.filter(i => !dialogueIntentKeys.has(`${i.type}:${i.target || ""}`))
            : response.intents;
          if (dedupedIntents.length) {
            loggerRef.current?.logIntents(dedupedIntents);
            intentResolverRef.current.resolve(dedupedIntents);
          }
        }

        // Timed events → timer system
        if (response.timedEvents?.length && timerRef.current) {
          timerRef.current.scheduleEvents(response.timedEvents);
        }

        // State updates — server is authoritative for encounter state,
        // but we apply alert level locally for immediate UI feedback
        if (response.stateUpdates) {
          loggerRef.current?.logStateChange(response.stateUpdates);
        }
        if (response.stateUpdates?.alertLevel) {
          const validLevels: AlertLevel[] = ["normal", "yellow", "red"];
          const level = validLevels.includes(response.stateUpdates.alertLevel) ? response.stateUpdates.alertLevel : "normal";
          setAlertLevel(level);
          loggerRef.current?.logAlert(level);
        }

        // Mission resolution
        if (response.encounterResolved && response.resolutionSummary) {
          missionResolvedRef.current = true;
          if (ambientTimerRef.current) clearTimeout(ambientTimerRef.current);
          loggerRef.current?.logMissionEnd(
            response.resolutionType ?? "objective_complete",
            response.resolutionSummary,
          );
          setMissionResult({
            type: response.resolutionType ?? "objective_complete",
            summary: response.resolutionSummary,
          });
          addLogEntry(response.resolutionSummary);
          addEntry("system", "Mission complete.");
          clearSavedSession();
          setHasSave(false);
        }
      } catch (err) {
        console.error("[processGMResponse] Error processing AI response:", err);
        addEntry("system", "Bridge systems momentarily disrupted. Recovering...");
      }
    },
    [processDialogue, addEntry, addLogEntry]
  );

  // ── Ambient tick — disabled (was burning tokens on idle tabs) ──
  const scheduleAmbientTick = useCallback(() => {
    // no-op: ambient auto-progression removed
  }, []);

  // Clean up ambient timer on unmount
  useEffect(() => {
    return () => {
      if (ambientTimerRef.current) clearTimeout(ambientTimerRef.current);
    };
  }, []);

  // ── Handle order completion → AI call ──
  const handleOrderComplete = useCallback(
    async (orderId: string, label: string) => {
      const sid = sessionIdRef.current;
      if (!sid) return;
      loggerRef.current?.logOrder("complete", orderId, label);

      try {
        const res = await fetch(`${API_BASE}/api/order-complete`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            sessionId: sid,
            orderId,
            label,
            currentSpatial: executorRef.current?.currentSpatialState,
          }),
        });
        if (res.ok) {
          const data = await res.json();
          if (data.encounterState) {
            setEncounterState(data.encounterState);
            if (data.encounterState.spatialState && executorRef.current) {
              executorRef.current.updateSpatialState(data.encounterState.spatialState);
            }
          }
          if (data.response) processGMResponse(data.response);
        }
      } catch {
        // Non-blocking
      }
    },
    [processGMResponse]
  );

  // ── Handle timed event fired ──
  const handleTimedEvent = useCallback(
    (event: { dialogue?: DialogueLine[]; intents?: GameIntent[]; stateUpdates?: any }) => {
      if (event.dialogue?.length) processDialogue(event.dialogue);
      if (event.intents?.length && intentResolverRef.current) {
        intentResolverRef.current.resolve(event.intents);
      }
    },
    [processDialogue]
  );

  // ── Game loop ──
  useEffect(() => {
    if (!executorRef.current || !timerRef.current) return;

    const executor = executorRef.current;
    const timer = timerRef.current;
    let lastSyncTime = 0;

    const loop = (time: number) => {
      const delta = lastTimeRef.current ? (time - lastTimeRef.current) / 1000 : 0;
      lastTimeRef.current = time;

      const clampedDelta = Math.min(delta, 0.1);

      executor.tick(clampedDelta);
      timer.tick(clampedDelta);
      combatSimRef.current?.tick(clampedDelta);
      ambientRef.current?.tick(clampedDelta);

      // Log spatial snapshot for mission replay
      loggerRef.current?.snapshotSpatial(executor.currentSpatialState);

      // Sync to React once per second (orders panel + spatial state for ship status)
      if (time - lastSyncTime > 1000) {
        lastSyncTime = time;
        setOrders([...timer.orders]);
        setLiveSpatial(structuredClone(executor.currentSpatialState));
      }

      rafRef.current = requestAnimationFrame(loop);
    };

    rafRef.current = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(rafRef.current);
  }, [sessionId]); // Re-init loop when session starts

  // ── Cancel order ──
  const cancelOrder = useCallback((orderId: string) => {
    timerRef.current?.cancelOrder(orderId);
    // Stop the ship — this will also trigger the onStop callback which cancels remaining orders
    executorRef.current?.enqueue([
      { action: "all_stop", params: { entityId: "player_ship" }, delay: 0 },
    ]);
  }, []);

  // ── Resume saved session ──
  const resumeSession = useCallback(() => {
    const saved = loadSavedSession();
    if (!saved) return;
    setSessionId(saved.sessionId);
    setEncounterState(saved.encounterState);
    setNarrativeLog(saved.narrativeLog);
    setAlertLevel(saved.alertLevel);
    setCaptainsLog(saved.captainsLog ?? []);
    setMissionStartMs(saved.missionStartMs || Date.now());
    if (saved.lockedTargets?.length) setLockedTargets(new Set(saved.lockedTargets));

    // Initialize mission logger for resumed session
    const logger = new MissionLogger(saved.sessionId, saved.encounterState);
    loggerRef.current = logger;

    const spatial = saved.encounterState.spatialState || DEFAULT_SPATIAL;
    const executor = new SceneCommandExecutor(spatial, saved.encounterState.playerShip?.name, saved.encounterState.playerShip?.faction);
    executor.setAlertLevelCallback(setAlertLevel);
    executor.setLogger(logger);
    executorRef.current = executor;
    const timer = new TimerSystem();
    executor.setOnStopCallback(() => timer.cancelAllOrders());
    timer.setOnOrderComplete(handleOrderComplete);
    timer.setOnEventFired(handleTimedEvent);
    timerRef.current = timer;
    intentResolverRef.current = new IntentResolver(executor, timer, {
      setAlertLevel,
      setViewscreenMode: (mode) => {
        setViewscreenMode(mode);
        loggerRef.current?.logViewscreen(mode);
        if (mode.mode === "hail" && mode.characterId) setViewscreenImage(getPortraitUrl(mode.characterId));
        if (mode.mode === "pending_hail") soundSystem.incomingHail();
        if (mode.mode === "space") setViewscreenImage(null);
      },
      setHailState: (state) => { setHailState(state); loggerRef.current?.logHail(state); },
      fetchPortrait,
      addLogEntry,
      setShowChart,
      addLockedTarget,
      updateShipSystems: (updater) => {
        setEncounterState((prev) => {
          if (!prev) return prev;
          const next = structuredClone(prev);
          updater(next.playerShip);
          return next;
        });
      },
    });
    executor.setEncounterState(saved.encounterState);
    combatSimRef.current = new CombatSimulator(
      () => intentResolverRef.current,
      () => encounterRef.current,
      () => executorRef.current,
    );
    ambientRef.current = new AmbientEventSystem(
      () => encounterRef.current,
      () => executorRef.current,
      addEntry,
    );
    addEntry("system", "Mission resumed.");
    scheduleAmbientTick();
  }, [addEntry, addLogEntry, fetchPortrait, handleOrderComplete, handleTimedEvent, scheduleAmbientTick]);

  // ── Start session ──
  const startSession = useCallback(async () => {
    ttsService.stop();
    ttsService.resetNpcVoices();
    missionResolvedRef.current = false;
    clearSavedSession();
    setHasSave(false);
    setMissionStartMs(Date.now());
    setIsLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/session`, { method: "POST" });
      const data = await res.json();
      setSessionId(data.sessionId);
      setEncounterState(data.encounterState);
      const initAlert: AlertLevel = (["normal", "yellow", "red"] as AlertLevel[]).includes(data.encounterState.alertLevel)
        ? data.encounterState.alertLevel
        : "normal";
      setAlertLevel(initAlert);

      // Initialize mission logger
      const logger = new MissionLogger(data.sessionId, data.encounterState);
      loggerRef.current = logger;

      // Initialize systems
      const spatial = data.encounterState.spatialState || DEFAULT_SPATIAL;
      const executor = new SceneCommandExecutor(spatial, data.encounterState.playerShip?.name, data.encounterState.playerShip?.faction);
      executor.setAlertLevelCallback(setAlertLevel);
      executor.setLogger(logger);
      executorRef.current = executor;

      const timer = new TimerSystem();

      // Only cancel orders on explicit all_stop (stopEntity internal=false)
      executor.setOnStopCallback((_entityId) => {
        timer.cancelAllOrders();
      });
      timer.setOnOrderComplete(handleOrderComplete);
      timer.setOnEventFired(handleTimedEvent);
      timerRef.current = timer;

      // IntentResolver translates high-level intents → scene commands
      intentResolverRef.current = new IntentResolver(executor, timer, {
        setAlertLevel: (level) => {
          setAlertLevel(level);
          loggerRef.current?.logAlert(level);
          if (level === "red")    addLogEntry("Red alert declared. All hands to battle stations.");
          else if (level === "yellow") addLogEntry("Yellow alert. Standing by all systems.");
          else                   addLogEntry("Standing down. Returning to normal operations.");
        },
        setViewscreenMode: (mode) => {
          setViewscreenMode(mode);
          loggerRef.current?.logViewscreen(mode);
          if (mode.mode === "hail") {
            addLogEntry(`Communications channel opened.`);
            if (mode.characterId) setViewscreenImage(getPortraitUrl(mode.characterId));
          }
          if (mode.mode === "pending_hail") {
            soundSystem.incomingHail();
          }
          if (mode.mode === "space") { setViewscreenImage(null); addLogEntry("Channel closed."); }
        },
        setHailState: (state) => { setHailState(state); loggerRef.current?.logHail(state); },
        fetchPortrait,
        addLogEntry,
        setShowChart,
        addLockedTarget,
        updateShipSystems: (updater) => {
          setEncounterState((prev) => {
            if (!prev) return prev;
            const next = structuredClone(prev);
            updater(next.playerShip);
            return next;
          });
        },
      });
      executor.setEncounterState(data.encounterState);
      combatSimRef.current = new CombatSimulator(
        () => intentResolverRef.current,
        () => encounterRef.current,
        () => executorRef.current,
      );
      ambientRef.current = new AmbientEventSystem(
        () => encounterRef.current,
        () => executorRef.current,
        addEntry,
      );

      // Process opening
      if (data.opening) {
        processGMResponse(data.opening);
      }
      addLogEntry(`Mission commenced. ${data.encounterState?.scenario ?? "Encounter initiated."}`);
      scheduleAmbientTick();
    } catch (err) {
      addEntry("system", `Connection failed: ${err}`);
    } finally {
      setIsLoading(false);
    }
  }, [addEntry, addLogEntry, processGMResponse, handleOrderComplete, handleTimedEvent, scheduleAmbientTick]);

  // ── Load pre-created encounter (from galaxy map warp) ──
  const loadEncounterData = useCallback((data: PreloadedEncounter) => {
    ttsService.stop();
    ttsService.resetNpcVoices();
    clearSavedSession();
    setHasSave(false);
    setMissionStartMs(Date.now());
    setSessionId(data.sessionId);
    setEncounterState(data.encounterState);
    const initAlert: AlertLevel = (["normal", "yellow", "red"] as AlertLevel[]).includes(data.encounterState.alertLevel)
      ? data.encounterState.alertLevel
      : "normal";
    setAlertLevel(initAlert);

    // Initialize mission logger for loaded encounter
    const logger = new MissionLogger(data.sessionId, data.encounterState);
    loggerRef.current = logger;

    const spatial = data.encounterState.spatialState || DEFAULT_SPATIAL;
    const executor = new SceneCommandExecutor(spatial, data.encounterState.playerShip?.name, data.encounterState.playerShip?.faction);
    executor.setAlertLevelCallback(setAlertLevel);
    executor.setLogger(logger);
    executorRef.current = executor;

    const timer = new TimerSystem();
    executor.setOnStopCallback(() => timer.cancelAllOrders());
    timer.setOnOrderComplete(handleOrderComplete);
    timer.setOnEventFired(handleTimedEvent);
    timerRef.current = timer;

    intentResolverRef.current = new IntentResolver(executor, timer, {
      setAlertLevel: (level) => {
        setAlertLevel(level);
        loggerRef.current?.logAlert(level);
        if (level === "red") addLogEntry("Red alert declared. All hands to battle stations.");
        else if (level === "yellow") addLogEntry("Yellow alert. Standing by all systems.");
        else addLogEntry("Standing down. Returning to normal operations.");
      },
      setViewscreenMode: (mode) => {
        setViewscreenMode(mode);
        loggerRef.current?.logViewscreen(mode);
        if (mode.mode === "hail") {
          addLogEntry("Communications channel opened.");
          if (mode.characterId) setViewscreenImage(getPortraitUrl(mode.characterId));
        }
        if (mode.mode === "pending_hail") soundSystem.incomingHail();
        if (mode.mode === "space") { setViewscreenImage(null); addLogEntry("Channel closed."); }
      },
      setHailState: (state) => { setHailState(state); loggerRef.current?.logHail(state); },
      fetchPortrait,
      addLogEntry,
      setShowChart,
      addLockedTarget,
      updateShipSystems: (updater) => {
        setEncounterState((prev) => {
          if (!prev) return prev;
          const next = structuredClone(prev);
          updater(next.playerShip);
          return next;
        });
      },
    });
    executor.setEncounterState(data.encounterState);
    combatSimRef.current = new CombatSimulator(
      () => intentResolverRef.current,
      () => encounterRef.current,
      () => executorRef.current,
    );
    ambientRef.current = new AmbientEventSystem(
      () => encounterRef.current,
      () => executorRef.current,
      addEntry,
    );

    if (data.opening) {
      processGMResponse(data.opening);
    }
    addLogEntry(`Mission commenced. ${data.encounterState?.scenario ?? "Encounter initiated."}`);
    scheduleAmbientTick();
  }, [addLogEntry, processGMResponse, handleOrderComplete, handleTimedEvent, addEntry, fetchPortrait, scheduleAmbientTick]);

  // Auto-initialize from preloaded data
  const preloadedRef = useRef<PreloadedEncounter | undefined>(undefined);
  useEffect(() => {
    if (preloaded && preloaded !== preloadedRef.current && !sessionId) {
      preloadedRef.current = preloaded;
      loadEncounterData(preloaded);
    }
  }, [preloaded, sessionId, loadEncounterData]);

  // ── Send player action ──
  // ── Optimistic command acknowledgment ──
  const applyOptimisticUpdate = useCallback((action: string) => {
    const lower = action.toLowerCase().trim();
    const enc = encounterRef.current;
    if (!enc) return;
    const crewByRole: Record<string, string> = {};
    for (const c of enc.crew ?? []) {
      const roleKey = c.role.toLowerCase().replace(/\s+/g, "_");
      crewByRole[roleKey] = c.name;
    }
    const tactical = crewByRole["tactical_officer"] ?? crewByRole["tactical"] ?? "Tactical";
    const helm = crewByRole["helm_officer"] ?? crewByRole["helm"] ?? "Helm";
    const science = crewByRole["science_officer"] ?? crewByRole["science"] ?? "Science";
    const comms = crewByRole["communications_officer"] ?? crewByRole["communications"] ?? "Comms";

    const patterns: Array<{ match: RegExp; speaker: string; role: string; ack: string; localAction?: () => void }> = [
      {
        match: /\b(raise shields|shields up)\b/i,
        speaker: tactical, role: "tactical",
        ack: "Shields up.",
        localAction: () => executorRef.current?.enqueue([{ action: "raise_shields", params: { entityId: "player_ship" }, delay: 0 }]),
      },
      {
        match: /\b(lower shields|shields down|drop shields)\b/i,
        speaker: tactical, role: "tactical",
        ack: "Lowering shields.",
        localAction: () => executorRef.current?.enqueue([{ action: "lower_shields", params: { entityId: "player_ship" }, delay: 0 }]),
      },
      {
        match: /\bred alert\b/i,
        speaker: tactical, role: "tactical",
        ack: "Red alert — all hands to battle stations.",
        localAction: () => {
          setAlertLevel("red");
          executorRef.current?.enqueue([
            { action: "set_alert", params: { level: "red" }, delay: 0 },
            { action: "raise_shields", params: { entityId: "player_ship" }, delay: 0 },
          ]);
        },
      },
      {
        match: /\byellow alert\b/i,
        speaker: tactical, role: "tactical",
        ack: "Yellow alert.",
        localAction: () => {
          setAlertLevel("yellow");
          executorRef.current?.enqueue([{ action: "set_alert", params: { level: "yellow" }, delay: 0 }]);
        },
      },
      {
        match: /\b(stand down|cancel alert)\b/i,
        speaker: tactical, role: "tactical",
        ack: "Standing down.",
        localAction: () => {
          setAlertLevel("normal");
          executorRef.current?.enqueue([
            { action: "set_alert", params: { level: "normal" }, delay: 0 },
            { action: "lower_shields", params: { entityId: "player_ship" }, delay: 0 },
          ]);
        },
      },
      {
        match: /\b(all stop|full stop)\b/i,
        speaker: helm, role: "helm",
        ack: "All stop, aye.",
        localAction: () => executorRef.current?.enqueue([{ action: "all_stop", params: { entityId: "player_ship" }, delay: 0 }]),
      },
      { match: /\b(scan|analyze|sensor sweep)\b/i, speaker: science, role: "science", ack: "Initiating scan." },
      { match: /\bhail\b/i, speaker: comms, role: "communications", ack: "Opening hailing frequencies." },
      { match: /\bevasive\b/i, speaker: helm, role: "helm", ack: "Evasive maneuvers, aye!" },
      { match: /\b(fire|torpedo|phaser|shoot|weapons? free)\b/i, speaker: tactical, role: "tactical", ack: "Weapons firing." },
    ];

    for (const p of patterns) {
      if (p.match.test(lower)) {
        addEntry("dialogue", p.ack, p.speaker, p.role);
        p.localAction?.();
        return;
      }
    }
    // Generic fallback
    addEntry("dialogue", "Aye, Captain.", helm, "helm");
  }, [addEntry, setAlertLevel]);

  const sendAction = useCallback(
    async (action: string) => {
      if (!sessionId || isBusy) return;

      ttsService.stop();
      scheduleAmbientTick(); // Reset idle timer on player action
      ambientRef.current?.onPlayerAction();
      addEntry("player", action, "Captain", "captain");

      // If a non-conversational overlay is up, clear it as soon as the player
      // issues a new command — the player has moved on. We keep `hail`,
      // `pending_hail`, and `chart` because those are explicit conversations
      // / navigation tools, not info readouts.
      const m = viewscreenMode.mode;
      if (m === "scan_readout" || m === "surface_map" || m === "magnify" || m === "recording" || m === "tactical") {
        setViewscreenMode({ mode: "space" });
        setCameraMode("follow");
      }

      // Phase 3.3: auto-mute heuristic. If the player addresses a crew member
      // by name or role while a hail is active, treat this command as a
      // private bridge conversation (channel muted) so the NPC doesn't hear
      // it. If they address the hailed NPC (or no one specifically) the
      // channel reopens automatically.
      let effectiveMute = hailState.channelMuted;
      if (hailState.active) {
        const lower = action.toLowerCase();
        const crew = encounterState?.crew ?? [];
        const addressedToCrew = crew.some(c => {
          const names = [
            c.name.toLowerCase(),
            c.name.split(/\s+/).pop()?.toLowerCase() ?? "",
            c.role.toLowerCase(),
            c.role.toLowerCase().replace(/\s+officer$/i, "").trim(),
          ].filter(Boolean);
          return names.some(n => n.length >= 3 && lower.includes(n));
        });
        const addressedToHailed = hailState.targetName
          ? lower.includes(hailState.targetName.toLowerCase().split(/\s+/).pop() ?? "")
          : false;

        if (addressedToCrew && !addressedToHailed) {
          effectiveMute = true;
          if (!hailState.channelMuted) {
            addEntry("system", "→ Bridge crew. Channel muted for this exchange.", undefined, undefined, true);
            setHailState(prev => ({ ...prev, channelMuted: true }));
          }
        } else if (addressedToHailed && hailState.channelMuted) {
          effectiveMute = false;
          addEntry("system", "→ Channel reopened.", undefined, undefined, true);
          setHailState(prev => ({ ...prev, channelMuted: false }));
        }
      }

      applyOptimisticUpdate(action);
      loggerRef.current?.logPlayerAction(action);
      setIsBusy(true);

      try {
        const res = await fetch(`${API_BASE}/api/action`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
          sessionId,
          action,
          setMuted: effectiveMute,
          currentSpatial: executorRef.current?.currentSpatialState,
        }),
        });

        if (!res.ok) {
          addEntry("system", `Error: ${res.status} ${res.statusText}`);
          return;
        }

        const data = await res.json();

        if (data.encounterState) {
          // Merge server encounter state but preserve executor's live playerShip
          // (speed, heading, position, shields) — the server state is stale by
          // the time the response arrives, and the executor is authoritative for
          // real-time movement state.
          const serverState = data.encounterState;
          if (executorRef.current && serverState.spatialState) {
            const live = executorRef.current.currentSpatialState;
            serverState.spatialState.playerShip = {
              ...serverState.spatialState.playerShip,
              position: live.playerShip.position,
              heading: live.playerShip.heading,
              speed: live.playerShip.speed,
              shields: live.playerShip.shields,
            };
            executorRef.current.updateSpatialState(serverState.spatialState);
          }
          setEncounterState(serverState);
          executorRef.current?.setEncounterState(serverState);
          if (data.encounterState.hailState) {
            setHailState(data.encounterState.hailState);
          }
        }

        if (data.response) {
          processGMResponse(data.response);
        }
      } catch (err) {
        addEntry("system", `Connection error: ${err}`);
      } finally {
        setIsBusy(false);
      }
    },
    [sessionId, isBusy, hailState, addEntry, processGMResponse, scheduleAmbientTick, viewscreenMode.mode, applyOptimisticUpdate, encounterState?.crew]
  );

  // ── Toggle mute ──
  const toggleMute = useCallback(() => {
    setHailState((prev) => {
      if (!prev.active) return prev;
      const next = { ...prev, channelMuted: !prev.channelMuted };
      return next;
    });
  }, []);

  // ── Accept / decline incoming hail ──
  const acceptHail = useCallback(() => {
    if (viewscreenMode.mode !== "pending_hail") return;
    ttsService.stop();
    const characterId = viewscreenMode.characterId;
    setViewscreenMode({ mode: "hail", characterId, portraitPrompt: viewscreenMode.portraitPrompt });
    if (characterId) {
      setViewscreenImage(getPortraitUrl(characterId));
      const entity = liveSpatial?.entities.find((e) => e.id === characterId);
      const party = encounterState?.parties.find((p) => p.id === characterId);
      const formattedId = characterId.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
      const newHailState: HailState = {
        active: true,
        channelMuted: false,
        targetId: characterId,
        targetName: party?.name ?? entity?.name ?? formattedId,
      };
      setHailState(newHailState);
      loggerRef.current?.logHail(newHailState);
    }
    addLogEntry("Communications channel opened.");
    soundSystem.incomingHail();
  }, [viewscreenMode, liveSpatial, encounterState, addLogEntry]);

  const declineHail = useCallback(() => {
    if (viewscreenMode.mode !== "pending_hail") return;
    setViewscreenMode({ mode: "space" });
    addLogEntry("Incoming hail declined.");
  }, [viewscreenMode, addLogEntry]);

  // ── Dismiss whatever overlay is on the viewscreen ──
  // Used by auto-dismiss timers (scan readout etc.) and the END HAIL button.
  // Atomically clears viewscreen mode, portrait image, and hail state so the
  // three can never desync again.
  const dismissViewscreen = useCallback(() => {
    setViewscreenMode({ mode: "space" });
    setViewscreenImage(null);
    setHailState({ active: false, channelMuted: false, targetId: null, targetName: null });
    setCameraMode("follow");
  }, []);

  // Safety net: if hail state goes inactive while viewscreen is still in hail mode,
  // collapse them. Prevents portrait-stuck-after-channel-closed bugs.
  useEffect(() => {
    if (!hailState.active && viewscreenMode.mode === "hail") {
      setViewscreenMode({ mode: "space" });
      setViewscreenImage(null);
      setCameraMode("follow");
    }
  }, [hailState.active, viewscreenMode.mode]);

  // Combat suppression: while we're being shot at, non-hail overlays
  // (scan_readout, magnify, surface_map, recording) get force-cleared so the
  // player sees the actual fight. Hails are intentional player choices and
  // are NOT auto-dismissed during combat — pulling them mid-conversation
  // would be more disorienting than the portrait blocking the view.
  const lastPlayerHitMsRef = useRef(0);
  const COMBAT_SUPPRESS_WINDOW_MS = 10_000;

  useEffect(() => {
    if (!executorRef.current) return;
    const cb = (entityId: string) => {
      if (entityId !== "player_ship") return;
      lastPlayerHitMsRef.current = Date.now();
      const m = viewscreenMode.mode;
      if (m === "scan_readout" || m === "magnify" || m === "surface_map" || m === "recording") {
        // Use the lighter clear (don't trash hail state — there isn't one)
        setViewscreenMode({ mode: "space" });
        setCameraMode("follow");
      }
    };
    executorRef.current.setOnPlayerHitCallback?.(cb);
    return () => executorRef.current?.setOnPlayerHitCallback?.(null);
  }, [viewscreenMode.mode, sessionId]);

  // If a non-hail overlay opens within 10s of the last hit, immediately close it.
  // This catches the case where the LLM emits a `viewscreen_mode: scan_readout`
  // intent right after the cruiser fires — without this we'd dismiss on the
  // hit and then the LLM re-opens the overlay one tick later.
  useEffect(() => {
    const m = viewscreenMode.mode;
    if (m !== "scan_readout" && m !== "magnify" && m !== "surface_map" && m !== "recording") return;
    const since = Date.now() - lastPlayerHitMsRef.current;
    if (since < COMBAT_SUPPRESS_WINDOW_MS) {
      setViewscreenMode({ mode: "space" });
      setCameraMode("follow");
    }
  }, [viewscreenMode.mode]);

  const downloadMissionLog = useCallback(() => {
    loggerRef.current?.download();
  }, []);

  return {
    sessionId,
    encounterState,
    narrativeLog,
    isLoading,
    isStreaming: isBusy,
    alertLevel,
    orders,
    liveSpatial,
    viewscreenMode,
    viewscreenImage,
    hailState,
    executor: executorRef.current,
    captainsLog,
    baseStardate,
    missionStartMs,
    hasSave,
    startSession,
    resumeSession,
    sendAction,
    toggleMute,
    cancelOrder,
    acceptHail,
    declineHail,
    showChart,
    setShowChart,
    lockedTargets,
    removeLockedTarget,
    missionResult,
    downloadMissionLog,
    dismissViewscreen,
  };
}
